import { useEffect } from 'react';
import { Alert, Grid } from '@mui/material';
import { theme } from 'theme';
import { useLocation, useNavigate, useParams } from 'react-router';
import { OrderNewProductOrService } from 'views/OrderNewProductOrService/OrderNewProductOrService';
import { useDispatch, useSelector } from 'react-redux';
import { selectAccountNumber, selectBrUid, selectSelectedAccountTableItem } from 'selectors/accountSelector';
import { setAccountNumber, setBrUid } from 'reducers/accountReducer';
import ProductConfiguration from 'views/ProductConfiguration/ProductConfiguration';
import ProductDetails from 'views/ProductDetails/ProductDetails';
import { AccountShownPageType } from 'types';
import { IndexBalanced } from 'views/Accounts/IndexBalanced/IndexBalanced';
import { DiscretionaryMandate } from 'views/DiscretionaryMandate/DiscretionaryMandate';
import { CaseOverview } from 'views/Case/CaseOverview';
import { ProductsAndServices } from 'views/ProductsAndServices/ProductsAndServices';
import { AddProductOrService } from 'views/AddProductsOrServices/AddProductsOrServices';
import { setIsEligible } from 'reducers/partnerStatusReducer';
import ServiceRoleDetails from 'components/productDetails/ServiceRoleDetails';
import { AppDispatch, RootState } from 'store';
import { getIsBrEligible } from 'services/partner/getIsBrEligible';
import { fetchAllServiceAgreementsForAccount, getServiceAgreement } from 'thunks/accountServiceAgreementsThunk';
import ProductModification from 'views/ProductModification/ProductModification';
import { fetchDocumentsToDistribute } from 'thunks/productDocumentsThunk';
import { LoadingSpinner } from 'components/loadingSpinner/LoadingSpinner';
import { useBusinessRelationTypeGate } from 'hooks/queries/businessRelationType';
import { closeModal, openModal } from '../../reducers/modalReducer';

const styles = {
  container: {
    minHeight: '100vh',
    padding: 0,
  },
  innerContainer: {
    paddingTop: theme.spacing(1),
    height: '100vh',
    overflowY: 'auto',
  },
  overviewProductsContainer: {
    backgroundColor: theme.palette.common.white,
    p: 1,
  },
  brTypeWarningContainer: {
    p: 1,
    pb: 0,
  },
};

type Props = {
  shownPage?: AccountShownPageType;
};

export const Accounts = ({ shownPage }: Props) => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const accountNumberFromStore = useSelector(selectAccountNumber);
  const brUidfromStore = useSelector(selectBrUid);
  const { serviceAgreementUid, processInstanceId, brUid } = useParams();
  const location = useLocation();
  const selectedProductId = useSelector(selectSelectedAccountTableItem);
  const { selectedServiceAgreement } = useSelector((state: RootState) => state.serviceAgreements);

  const searchParams = new URLSearchParams(location.search);
  const accountNumberFromSearchParam = searchParams.get('accountNumber');
  const brUidFromSearchParam = searchParams.get('brUid');
  const resolvedBrUid = brUid ?? brUidFromSearchParam ?? undefined;
  const { canLoadAccountData, isBrTypeUnavailable, isPrivateBankingBr, isWaitingForCurrentBrType } =
    useBusinessRelationTypeGate(resolvedBrUid);

  useEffect(() => {
    if (resolvedBrUid && isPrivateBankingBr) {
      navigate(`/private-banking/${resolvedBrUid}/`, { replace: true });
    }
  }, [isPrivateBankingBr, navigate, resolvedBrUid]);

  useEffect(() => {
    if (!canLoadAccountData) {
      return;
    }

    if (selectedServiceAgreement) {
      return;
    }

    if (serviceAgreementUid) {
      dispatch(getServiceAgreement(serviceAgreementUid, null, true));
    } else if (processInstanceId) {
      dispatch(getServiceAgreement(undefined, processInstanceId, true));
    }
  }, [selectedServiceAgreement, serviceAgreementUid, processInstanceId, dispatch, canLoadAccountData]);

  useEffect(() => {
    if (accountNumberFromSearchParam) {
      dispatch(setAccountNumber(accountNumberFromSearchParam));
    }

    if (brUidFromSearchParam) {
      dispatch(setBrUid(brUidFromSearchParam));
    }

    if (brUid) {
      dispatch(setBrUid(brUid));
    }
  }, [dispatch, brUidFromSearchParam, accountNumberFromSearchParam, brUid]);

  useEffect(() => {
    if (!canLoadAccountData) {
      return;
    }

    if (brUid) {
      dispatch(fetchAllServiceAgreementsForAccount(undefined, brUid, undefined, true));
    }
    if (brUidFromSearchParam) {
      dispatch(fetchAllServiceAgreementsForAccount(undefined, brUidFromSearchParam, undefined, true));
    }
    if (brUid) {
      dispatch(fetchDocumentsToDistribute(brUid, null));
    }
    if (brUidFromSearchParam) {
      dispatch(fetchDocumentsToDistribute(brUidFromSearchParam, null));
    }
  }, [brUid, dispatch, brUidFromSearchParam, canLoadAccountData]);

  useEffect(() => {
    const checkEligibility = async (id: string) => {
      try {
        const eligibility = await getIsBrEligible(id);

        dispatch(setIsEligible(eligibility));
      } catch (axiosError: unknown) {
        dispatch(
          openModal({
            modalType: 'JBErrorModalWrapper',
            modalProps: {
              httpError: axiosError,
              okHandler: () => {
                dispatch(closeModal());
              },
              technicalInfo: {
                title: 'Error when checking BR eligibility',
                userErrorMessage: 'An error occurred when checking BR eligibility. Please try again later.',
              },
            },
          }),
        );
      }
    };

    if (resolvedBrUid && canLoadAccountData) {
      checkEligibility(resolvedBrUid);
    }
  }, [dispatch, resolvedBrUid, canLoadAccountData]);

  if (isWaitingForCurrentBrType) {
    return <LoadingSpinner message="Loading account..." />;
  }

  if (isPrivateBankingBr) {
    return null;
  }

  const renderSwitch = (param?: AccountShownPageType) => {
    switch (param) {
      case AccountShownPageType.NEW_MANDATE:
        return <DiscretionaryMandate title={'NEW'} isPartner={false} id={accountNumberFromStore} />;
      case AccountShownPageType.CHANGE_MANDATE:
        return <DiscretionaryMandate title={'CHANGE'} isPartner={false} id={accountNumberFromStore} isChange={true} />;
      case AccountShownPageType.PRODUCT_CONFIGURATION:
        return <ProductConfiguration />;
      case AccountShownPageType.PRODUCT_DETAILS:
        return <ProductDetails />;
      case AccountShownPageType.SERVICE_ROLE:
        return <ServiceRoleDetails />;
      case AccountShownPageType.ORDER_NEW_PRODUCT_OR_SERVICE:
        return <AddProductOrService accountNumber={accountNumberFromStore} brUid={brUidfromStore} partnerId={null} />;
      case AccountShownPageType.PRODUCT_MODIFICATION:
        return <ProductModification brUid={brUid} partnerUid={null} />;
      case AccountShownPageType.PRODUCT:
        return <IndexBalanced productId={selectedProductId} />;
      case AccountShownPageType.CASE:
        return <CaseOverview productId={selectedProductId} />;
      default:
        return <OrderNewProductOrService brUid={brUidfromStore} partnerNumber={null} />;
    }
  };

  return (
    <Grid data-testid="accounts" component="main" container sx={styles.container}>
      {isBrTypeUnavailable && (
        <Grid item xs={12} sx={styles.brTypeWarningContainer}>
          <Alert data-testid="br-type-unavailable-message" severity="warning">
            Business relationship type is not available.
          </Alert>
        </Grid>
      )}
      <Grid
        item
        xs={6}
        sx={[styles.innerContainer, styles.overviewProductsContainer]}
        data-testid="left-side-container"
      >
        <ProductsAndServices partnerId={null} brUid={brUidfromStore} />
      </Grid>
      <Grid item xs={6} sx={styles.innerContainer} data-testid="right-side-container">
        {renderSwitch(shownPage)}
      </Grid>
    </Grid>
  );
};
